/**
 * OfflineIndicator — internet ulanish va sync holati indikatori
 *
 * Holatlar:
 *   - 🟢 Online + 0 pending → ko'rinmaydi (yashirin)
 *   - 🟢 Online + N pending → ko'k "Sinxronlanmoqda" badge
 *   - 🔴 Offline → qizil "Offline" badge + pending count
 */

import { useQuery } from '@tanstack/react-query';
import { RefreshCw, WifiOff } from 'lucide-react';
import { useEffect } from 'react';

import { cn } from '@/shared/lib/cn';

import { syncEngine } from './sync.engine';
import { useOnlineStatus } from './useOnlineStatus';

const PENDING_QUERY_KEY = ['offline', 'pending-count'] as const;

export function OfflineIndicator(): React.ReactElement | null {
  const { isOnline } = useOnlineStatus();

  const { data: pendingCount = 0, refetch: refetchPending } = useQuery({
    queryKey: PENDING_QUERY_KEY,
    queryFn: () => syncEngine.getPendingCount(),
    refetchInterval: 5_000,
  });

  // Online bo'lganda avtomatik sync
  useEffect(() => {
    if (!isOnline || pendingCount === 0) return;
    void syncEngine.sync().then(() => refetchPending());
  }, [isOnline, pendingCount, refetchPending]);

  // Subscribe to sync results
  useEffect(() => {
    return syncEngine.subscribe(() => {
      void refetchPending();
    });
  }, [refetchPending]);

  // Hech narsa ko'rsatilmaydi — online va hech narsa pending bo'lmasa
  if (isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {/* Offline */}
      {!isOnline && (
        <div
          className={cn(
            'flex items-center gap-1.5 rounded-full px-2.5 py-1',
            'bg-destructive/15 text-xs font-medium text-destructive',
          )}
          role="status"
          aria-live="polite"
        >
          <WifiOff className="size-3" />
          Offline
          {pendingCount > 0 && (
            <span className="ml-1 rounded-full bg-destructive/30 px-1.5 text-[10px]">
              {pendingCount}
            </span>
          )}
        </div>
      )}

      {/* Online + sync in progress */}
      {isOnline && pendingCount > 0 && (
        <div
          className={cn(
            'flex items-center gap-1.5 rounded-full px-2.5 py-1',
            'bg-info/15 text-xs font-medium text-info',
          )}
          role="status"
          aria-live="polite"
        >
          <RefreshCw className="size-3 animate-spin" />
          Sync ({pendingCount})
        </div>
      )}
    </div>
  );
}
